import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { API_CONFIG } from '../config/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import SelectDropdown from '../components/SelectDropdown';
import { formatDate } from '../utils/dateFormatter';

interface UserData {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone?: string;
  profile?: {
    firstName?: string;
    lastName?: string;
    preferredName?: string;
  };
  plan: string;
  hasSubscription: boolean;
  subscriptionId?: string;
  startDate?: string;
  endDate?: string;
  status: 'active' | 'expired' | 'cancelled' | 'free';
  createdAt: string;
  lastLogin?: string;
  isActive: boolean;
  role: string;
  location?: {
    province?: string;
    city?: string;
    barangay?: string;
  };
  isEmailVerified: boolean;
  authProvider: string;
  lastUpdated: string;
  lastActive: string;
  passwordChanged: string;
  loginAttempts: number;
}

const EditUserPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    role: 'User',
    isActive: true,
    isEmailVerified: false
  });

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!id) {
        setError('No user ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('User not found');
          } else if (response.status === 403) {
            throw new Error('Access denied. Admin privileges required.');
          } else {
            throw new Error('Failed to fetch user details');
          }
        }

        const fullResponse = await response.json();

        if (fullResponse.success && fullResponse.data) {
          const { user, subscription, history } = fullResponse.data;
          setUserData(user);
          
          // Populate form data
          setFormData({
            firstName: user.profile?.firstName || '',
            lastName: user.profile?.lastName || '',
            preferredName: user.profile?.preferredName || '',
            email: user.email || '',
            role: user.role || 'User',
            isActive: user.isActive,
            isEmailVerified: user.isEmailVerified || false
          });
        } else {
          throw new Error('Invalid response format');
        }

      } catch (err) {

        setError(err instanceof Error ? err.message : 'Failed to load user details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save
  const handleSave = async () => {
    if (!userData) return;

    try {
      setIsSaving(true);
      setError(null);

      const updateData = {
        profile: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          preferredName: formData.preferredName
        },
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
        isEmailVerified: formData.isEmailVerified
      };

      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${userData.id}/update-with-sync`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || `Failed to update user (${response.status})`);
      }

      toast.success('User updated successfully!');
      
      // Navigate back to subscriber detail page
      setTimeout(() => {
        navigate(`/panel/admin/subscriber-management/${userData.id}`);
      }, 500);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update user';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };


  // Handle breadcrumb navigation
  const handleBackToSubscribers = () => {
    navigate('/panel/admin/subscriber-management');
  };

  const handleBackToDetail = () => {
    if (userData) {
      navigate(`/panel/admin/subscriber-management/${userData.id}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="edit-user">
        <div className="edit-user__loading">
          <div className="edit-user__loading-spinner"></div>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !userData) {
    return (
      <div className="edit-user">
        <div className="edit-user__error">
          <h2>Error Loading User</h2>
          <p>{error}</p>
          <button
            className="edit-user__back-button"
            onClick={handleBackToSubscribers}
          >
            Back to Subscribers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`edit-user ${isDarkMode ? 'dark' : ''}`}>
      {/* Header with breadcrumb */}
      <header className="edit-user__header">
        <nav className="edit-user__breadcrumb">
          <button
            className="edit-user__breadcrumb-link"
            onClick={handleBackToSubscribers}
          >
            Subscribers
          </button>
          <span className="edit-user__breadcrumb-separator">/</span>
          <button
            className="edit-user__breadcrumb-link"
            onClick={handleBackToDetail}
          >
            {userData.name}
          </button>
          <span className="edit-user__breadcrumb-separator">/</span>
          <span className="edit-user__breadcrumb-current">
            Edit User
          </span>
        </nav>

        <div className="edit-user__header-content">
          <div className="edit-user__title-section">
            <h1 className="edit-user__title">{userData.name}</h1>
            <p className="edit-user__subtitle">Edit User Profile Details</p>
          </div>
        </div>
      </header>

      {/* Main content grid */}
      <main className="edit-user__content">
        {/* Left column - Form */}
        <div className="edit-user__left-column">
          <div className="edit-user__card">
            <div className="edit-user__card-header">
              <h2 className="edit-user__card-title">Profile Information</h2>
            </div>
            <div className="edit-user__card-content">
              <form className="edit-user__form">
                <div className="edit-user__form-grid">
                  <label className="edit-user__form-field">
                    <p className="edit-user__field-label">First Name</p>
                    <input
                      className="edit-user__input"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="Enter first name"
                    />
                  </label>

                  <label className="edit-user__form-field">
                    <p className="edit-user__field-label">Last Name</p>
                    <input
                      className="edit-user__input"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Enter last name"
                    />
                  </label>

                  <label className="edit-user__form-field edit-user__form-field--full">
                    <p className="edit-user__field-label">Preferred Name</p>
                    <input
                      className="edit-user__input"
                      type="text"
                      value={formData.preferredName}
                      onChange={(e) => handleInputChange('preferredName', e.target.value)}
                      placeholder="Enter preferred name"
                    />
                  </label>

                  <label className="edit-user__form-field edit-user__form-field--full">
                    <p className="edit-user__field-label">Email</p>
                    <input
                      className="edit-user__input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email address"
                    />
                  </label>

                  <div className="edit-user__form-field edit-user__form-field--full">
                    <p className="edit-user__field-label">Role</p>
                    <SelectDropdown
                      value={formData.role}
                      onChange={(value) => handleInputChange('role', value)}
                      options={[
                        { value: 'User', label: 'Subscriber' },
                        { value: 'Consultant', label: 'Consultant' },
                        { value: 'Admin', label: 'Administrator' },
                        { value: 'Enterprise', label: 'Enterprise' }
                      ]}
                      placeholder="Select role"
                      ariaLabel="Select user role"
                    />
                  </div>

                  <div className="edit-user__toggle-field edit-user__form-field--full">
                    <p className="edit-user__field-label">Is Active</p>
                    <button
                      type="button"
                      className={`edit-user__toggle ${formData.isActive ? 'edit-user__toggle--active' : ''}`}
                      onClick={() => handleInputChange('isActive', !formData.isActive)}
                      aria-checked={formData.isActive}
                      role="switch"
                    >
                      <span className="edit-user__toggle-indicator"></span>
                    </button>
                  </div>

                  <div className="edit-user__toggle-field edit-user__form-field--full">
                    <p className="edit-user__field-label">Is Email Verified</p>
                    <button
                      type="button"
                      className={`edit-user__toggle ${formData.isEmailVerified ? 'edit-user__toggle--active' : ''}`}
                      onClick={() => handleInputChange('isEmailVerified', !formData.isEmailVerified)}
                      aria-checked={formData.isEmailVerified}
                      role="switch"
                    >
                      <span className="edit-user__toggle-indicator"></span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="edit-user__card-footer">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isSaving}
                className="edit-user__save-button"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Right column - System info and location */}
        <div className="edit-user__right-column">
          {/* System Information Card */}
          <div className="edit-user__card">
            <div className="edit-user__card-header">
              <h3 className="edit-user__card-title">System Information</h3>
            </div>
            <div className="edit-user__info-list">
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">User ID</span>
                <span className="edit-user__info-value edit-user__info-value--mono">{userData.id}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Auth Provider</span>
                <span className="edit-user__info-value">{userData.authProvider || 'Email/Password'}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Created</span>
                <span className="edit-user__info-value">{formatDate(userData.createdAt)}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Last Updated</span>
                <span className="edit-user__info-value">{formatDate(userData.lastUpdated || userData.createdAt)}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Last Active</span>
                <span className="edit-user__info-value">{formatDate(userData.lastActive || userData.createdAt)}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Last Login</span>
                <span className="edit-user__info-value">{userData.lastLogin ? formatDate(userData.lastLogin) : 'Never'}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Password Changed</span>
                <span className="edit-user__info-value">{formatDate(userData.passwordChanged || userData.createdAt)}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Login Attempts</span>
                <span className="edit-user__info-value">{userData.loginAttempts || 0}</span>
              </div>
            </div>
          </div>

          {/* Location Card */}
          <div className="edit-user__card">
            <div className="edit-user__card-header">
              <h3 className="edit-user__card-title">Location</h3>
            </div>
            <div className="edit-user__info-list">
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Province</span>
                <span className="edit-user__info-value">{userData.location?.province || 'Not set'}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">City</span>
                <span className="edit-user__info-value">{userData.location?.city || 'Not set'}</span>
              </div>
              <div className="edit-user__info-item">
                <span className="edit-user__info-label">Barangay</span>
                <span className="edit-user__info-value">{userData.location?.barangay || 'Not set'}</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Error message */}
      {error && (
        <div className="edit-user__error-message">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default EditUserPage;

